// Synthetic hose stream, used to answer "does the map hold up at 5x / 10x?"
// without needing a Saffron estate of that size.
//
// It speaks the same `type@path:payload` grammar as the real server and drives
// the same code paths: a tree of apps/envs/machines/processes/roles delivered up
// front, then per-second resource and log-count events, plus scope objects
// created and deleted at a configurable rate.

const LOG_LEVEL_COUNT = 6;

const APP_NAMES = [
  "APLS",
  "ASSE",
  "BORG",
  "BUFF",
  "DIA",
  "EDEN",
  "EE",
  "EFD",
  "HUB",
  "LEAP",
  "MAPL",
  "PCPL",
  "PEP",
  "RECO",
  "REDG",
  "RW",
  "WILT",
  "i4b",
];

// Live first: the client applies a default envs=Live filter on connect, so a
// synthetic estate whose environments are sliced from this list has to include
// Live or the whole tree is filtered away client-side and arrives as orphans.
const ENV_NAMES = ["Live", "Test", "Dev"];

const ROLE_NAMES = [
  "webapi",
  "worker",
  "scheduler",
  "tunnel",
  "relay",
  "detailler",
];

const SCOPE_TYPES = ["HttpRequest", "Command", "Query"];

// Summaries of wildly differing length. A callout is sized from its text and the
// widest one on a side is what positions every leader on it, so uniform-width
// labels would hide that interaction entirely.
// Shaped like the real ones: an HTTP request's summary is the bare path, and
// commands and queries are named after their type.
const SUMMARIES = {
  HttpRequest: [
    "/api/orders",
    "/api/payments/authorise",
    "/api/customers/{id}/preferences",
    "/dashboard/recently-viewed",
    "/units/154/data-stream",
    "/session",
    "/api/inventory",
  ],
  Command: [
    "RebuildSearchIndexForTenantCommand",
    "DispatchOutboundNotificationBatch",
    "Time sync - 0207 603 3458",
    "heart-beat",
  ],
  Query: [
    "SELECT * FROM audit_log WHERE created_at > @since ORDER BY created_at DESC",
    "GetOutstandingReconciliationsQuery",
    "DashboardRecentlyViewedQuery",
  ],
};

// Health probes run against every service constantly, so they dominate scope
// traffic in a real estate — which is the whole reason the panel can exclude
// them, and why they have to be here to test that it works.
const HEALTH_SUMMARIES = ["/health/ready", "/health/live", "/health"];
const HEALTH_SHARE = 0.4;

function summaryFor(type, id, r) {
  if (type === "HttpRequest" && r < HEALTH_SHARE) {
    return HEALTH_SUMMARIES[Math.floor((r / HEALTH_SHARE) * 3) % 3];
  }

  const options = SUMMARIES[type] || SUMMARIES.Command;
  return options[Math.floor(r * options.length) % options.length] + " " + id;
}

// Sized so the map is legible rather than a wall of dots — the estate you want
// when looking at *where things are drawn*. For the "does it hold up at 10x"
// question these are all URL parameters: `?synth=1&apps=18&machines=3&vary=0`
// gets the old uniform stress estate back.
// Exponent on the fan-out draw. 1 would be uniform across the range; above that
// most nodes take the smallest count and the occasional one takes the largest.
const FAN_OUT_SKEW = 2.5;

// Apps guaranteed every environment, so the estate always has a couple of very
// wide wedges among the narrow ones whatever the seed happens to draw.
const ALWAYS_MULTI_ENV_APPS = 2;

const DEFAULTS = {
  apps: 7,
  // Ceilings rather than counts, and with the skew above most nodes sit at the
  // bottom of the range — so the estate stays small while a couple of branches
  // come out several times the size of the rest.
  envs: 3,
  machines: 3,
  procs: 3,
  roles: 4,
  cores: 4,
  scopeRate: 20, // scopes started per second across the whole estate
  scopeMs: 1500, // how long a scope lives before its delete is sent
  // 0 gives every node the full count, so every wedge is the same size and the
  // map comes out a near-perfect circle. Real estates are nothing like that, and
  // a perfect circle is the one shape that never tests placement — so by default
  // each node takes anywhere from one child up to its limit.
  vary: 1,
  // Proportion of scopes that are raised inside another scope rather than
  // directly on a role. Nested scopes sit further out than their parent, so
  // these are what stop every scope being the outermost thing at its bearing.
  nesting: 0.25,
};

function processData(app, env, pid) {
  return JSON.stringify({
    connection: "synth",
    commandLine: "/opt/" + app.toLowerCase() + "/" + app + ".dll",
    saffronVersion: "10.4.0",
    appVersion: "1.0.0",
    builtUtc: "2026-01-01T00:00:00Z",
    processStartUtc: "2026-01-01T00:00:00Z",
    runningAs: "root",
    internalIp: "10.0.0.1",
    pid,
    env,
  });
}

// Deterministic PRNG so two runs at the same parameters produce the same stream,
// which is what makes before/after comparison meaningful.
function makeRandom(seed) {
  let state = seed >>> 0 || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 4294967296;
  };
}

export class SynthSocket {
  constructor(options = {}) {
    this.binaryType = "blob";
    this.onopen = null;
    this.onmessage = null;
    this.onclose = null;
    this.onerror = null;

    this.opts = { ...DEFAULTS, ...options };
    this.random = makeRandom(options.seed || 12345);
    this.closed = false;
    this.timers = [];
    this.intervals = [];

    this.processes = []; // paths, for resource events
    this.roles = []; // paths, for counts events
    this.roleWeights = []; // cumulative, so scope traffic clusters
    this.roleWeightTotal = 0;
    this.liveScopes = []; // paths, so a scope can be raised inside another
    this.scopeSeq = 0;

    window.setTimeout(() => {
      if (!this.closed && this.onopen) this.onopen({});
    }, 0);
  }

  send(command) {
    if (this.closed) return;
    if (command.startsWith("auth ")) {
      this.emit("authok");
      // The client asks for root collections on authok; the tree is pushed
      // rather than waiting for each `list`, which is equivalent from the
      // client's point of view.
      window.setTimeout(() => this.buildTree(), 0);
    }
  }

  emit(data) {
    if (this.closed || !this.onmessage) return;
    this.onmessage({ data });
  }

  // Anywhere from one child up to `max`, skewed hard towards one.
  //
  // Drawing uniformly across the range made every branch middling, and middling
  // branches all look alike. A real estate has one app in three environments
  // across a dozen machines and a row of others with a single box each, and it is
  // that lopsidedness — a couple of very wide wedges among many narrow ones — that
  // actually exercises placement. `vary: 0` restores the fixed count.
  fanOut(max) {
    if (max <= 1) return max;
    const vary = this.opts.vary;
    if (!vary) return max;
    const min = Math.max(1, Math.round(max * (1 - vary)));
    const span = max - min;
    const skewed = Math.pow(this.random(), FAN_OUT_SKEW);
    return min + Math.min(span, Math.floor(skewed * (span + 1)));
  }

  buildTree() {
    if (this.closed) return;

    const o = this.opts;
    const apps = [];
    for (let i = 0; i < o.apps; i++) {
      apps.push(
        APP_NAMES[i % APP_NAMES.length] + (i >= APP_NAMES.length ? i : "")
      );
    }

    this.emit("items@apps:" + apps.join("\n"));

    for (let appIndex = 0; appIndex < apps.length; appIndex++) {
      const app = apps[appIndex];
      const appPath = "apps/" + app;
      this.emit("data@" + appPath + ":" + app + " application");

      // Live always, because the client applies a default envs=Live filter on
      // connect and an app without it would arrive and then vanish.
      //
      // The first two apps are given the full set outright rather than left to
      // the draw. One app across three environments beside a row of others with
      // only Live is the shape worth testing against, and with seven apps and a
      // fixed seed, leaving it to chance means some seeds produce no such app at
      // all — as this one did.
      const envCount =
        appIndex < ALWAYS_MULTI_ENV_APPS ? o.envs : this.fanOut(o.envs);
      const envs = ENV_NAMES.slice(0, envCount);
      this.emit("items@" + appPath + "/envs:" + envs.join("\n"));

      for (const env of envs) {
        const envPath = appPath + "/envs/" + env;

        const machines = [];
        const machineCount = this.fanOut(o.machines);
        for (let m = 0; m < machineCount; m++) {
          machines.push(app.toLowerCase() + "-" + env.toLowerCase() + "-" + m);
        }
        this.emit("items@" + envPath + "/machines:" + machines.join("\n"));

        for (const machine of machines) {
          const machinePath = envPath + "/machines/" + machine;
          // Machine data is colon-delimited "os:variant", not JSON (see getLogo
          // in schema/machine.js).
          this.emit("data@" + machinePath + ":Linux:ubuntu");

          const procs = [];
          const procCount = this.fanOut(o.procs);
          for (let p = 0; p < procCount; p++) procs.push(String(1000 + p));
          this.emit("items@" + machinePath + "/processes:" + procs.join("\n"));

          for (const proc of procs) {
            const procPath = machinePath + "/processes/" + proc;
            this.emit("data@" + procPath + ":" + processData(app, env, proc));
            this.processes.push(procPath);

            const roles = ROLE_NAMES.slice(0, this.fanOut(o.roles));
            this.emit("items@" + procPath + "/roles:" + roles.join("\n"));

            for (const role of roles) {
              const rolePath = procPath + "/roles/" + role;
              this.emit("data@" + rolePath + ":" + role + ":⚙");
              this.roles.push(rolePath);

              // Traffic in a real estate is nothing like even — a couple of
              // roles carry most of it. Spreading scopes uniformly would spread
              // the callouts evenly round the map, which is the easy case for
              // placement; clustering them is the hard one.
              const weight = this.opts.vary
                ? Math.pow(this.random(), 3) * 20 + 0.05
                : 1;
              this.roleWeightTotal += weight;
              this.roleWeights.push(this.roleWeightTotal);
            }
          }
        }
      }
    }

    console.log(
      "[synth]",
      this.processes.length,
      "processes,",
      this.roles.length,
      "roles"
    );

    this.startEvents();
  }

  pickRole() {
    if (!this.opts.vary || !this.roleWeightTotal) {
      return this.roles[Math.floor(this.random() * this.roles.length)];
    }
    const target = this.random() * this.roleWeightTotal;
    let lo = 0;
    let hi = this.roleWeights.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.roleWeights[mid] < target) lo = mid + 1;
      else hi = mid;
    }
    return this.roles[lo];
  }

  startEvents() {
    // Processes and roles each report once a second, matching ActivityHandler's
    // real cadence, so message rate scales with estate size exactly as it does
    // against a live server.
    this.intervals.push(
      window.setInterval(() => this.emitResources(), 1000),
      window.setInterval(() => this.emitCounts(), 1000)
    );

    const scopeRate = this.opts.scopeRate;
    if (scopeRate > 0) {
      // Batched into 10 ticks a second so the interval count stays sane at high
      // scope rates.
      const perTick = scopeRate / 10;
      this.intervals.push(
        window.setInterval(() => this.emitScopes(perTick), 100)
      );
    }
  }

  emitResources() {
    if (this.closed) return;
    const cores = this.opts.cores;
    for (const procPath of this.processes) {
      const user = this.random() * cores * 0.6;
      const kernel = this.random() * cores * 0.2;
      const threads = 20 + Math.floor(this.random() * 40);
      const mem = Math.floor((80 + this.random() * 200) * 1024 * 1024);
      this.emit(
        "event@" +
          procPath +
          ":resources|" +
          user.toFixed(3) +
          "," +
          kernel.toFixed(3) +
          "," +
          threads +
          "," +
          mem
      );
    }
  }

  emitCounts() {
    if (this.closed) return;
    for (const rolePath of this.roles) {
      const counts = [];
      let any = false;
      for (let i = 0; i < LOG_LEVEL_COUNT; i++) {
        // Weighted so low levels dominate, as in a real estate.
        const n =
          this.random() < 0.3 / (i + 1) ? 1 + Math.floor(this.random() * 3) : 0;
        if (n) any = true;
        counts.push(n);
      }
      if (any) this.emit("event@" + rolePath + ":counts|" + counts.join(","));
    }
  }

  emitScopes(count) {
    if (this.closed || !this.roles.length) return;

    // Fractional rates are handled by accumulating, so scopeRate=5 gives one
    // scope every other tick rather than none at all.
    this.scopeCarry = (this.scopeCarry || 0) + count;
    const toEmit = Math.floor(this.scopeCarry);
    this.scopeCarry -= toEmit;

    for (let i = 0; i < toEmit; i++) {
      // Most scopes hang off a role. The rest are raised inside a scope that is
      // already running, which puts them a ring further out — so a scope is not
      // always the outermost thing at its own bearing.
      let parentPath;
      if (this.liveScopes.length && this.random() < this.opts.nesting) {
        parentPath =
          this.liveScopes[Math.floor(this.random() * this.liveScopes.length)];
      } else {
        parentPath = this.pickRole();
      }

      const type = SCOPE_TYPES[Math.floor(this.random() * SCOPE_TYPES.length)];
      const id = "s" + this.scopeSeq++;
      const collection = type.toLowerCase() + "s";
      const scopePath = parentPath + "/" + collection + "/" + id;

      this.emit("items@" + parentPath + "/" + collection + ":" + id);
      this.emit(
        "data@" +
          scopePath +
          ':{"type":"' +
          type +
          '","id":"' +
          id +
          '","summary":"' +
          summaryFor(type, id, this.random()) +
          '"}'
      );

      this.liveScopes.push(scopePath);

      // Lifetimes spread over an order of magnitude, so callouts arrive and
      // leave at irregular intervals instead of marching through in lockstep.
      const life = this.opts.vary
        ? this.opts.scopeMs * (0.25 + Math.pow(this.random(), 2) * 3)
        : this.opts.scopeMs;

      const timer = window.setTimeout(() => {
        if (this.closed) return;
        // A quarter fail, so the callout overflow ranking has something to rank.
        const outcome = this.random() < 0.25 ? 0 : 1;
        this.emit("event@" + scopePath + ':end|{"outcome":' + outcome + "}");
        this.emit("delete@" + scopePath);
        const at = this.liveScopes.indexOf(scopePath);
        if (at >= 0) this.liveScopes.splice(at, 1);
      }, life);
      this.timers.push(timer);
    }

    // The timer list would otherwise grow without bound over a long run.
    if (this.timers.length > 10000) {
      this.timers = this.timers.slice(-5000);
    }
  }

  close() {
    this.closed = true;
    for (const t of this.timers) window.clearTimeout(t);
    for (const i of this.intervals) window.clearInterval(i);
    this.timers.length = 0;
    this.intervals.length = 0;
    if (this.onclose) this.onclose({});
  }
}
